import { Link } from 'react-router-dom';
import { HelpCircle, Mail, MessageCircle, FileText, CreditCard, User, ArrowLeft, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

export default function HelpCenter() {
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();

    const categories = [
        {
            icon: User,
            title: t('help.categories.account.title'),
            desc: t('help.categories.account.desc'),
            link: "/login"
        },
        {
            icon: CreditCard,
            title: t('help.categories.billing.title'),
            desc: t('help.categories.billing.desc'),
            link: "/pricing"
        },
        {
            icon: FileText,
            title: t('help.categories.content.title'),
            desc: t('help.categories.content.desc'),
            link: "/study-guide"
        },
        {
            icon: MessageCircle,
            title: t('help.categories.support.title'),
            desc: t('help.categories.support.desc'),
            link: "/legal/contact"
        }
    ];

    const faqs = [
        {
            q: t('help.faq.q1'),
            a: t('help.faq.a1')
        },
        {
            q: t('help.faq.q2'),
            a: t('help.faq.a2')
        },
        {
            q: t('help.faq.q3'),
            a: t('help.faq.a3')
        }
    ];

    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
            }
        }))
    };

    return (
        <div className="min-h-screen bg-dark-bg text-text-main font-sans">
            <SEO
                title={t('help.title')}
                description={t('help.seo_description') || "Centre d'aide CitoyenPro - FAQ et Support."}
                keywords="aide citoyenneté canadienne, support citoyenpro, faq examen canada"
                canonical="/help-center"
                schema={schema}
            />
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-2xl font-bold text-text-main">CitoyenPro</span>
                    </Link>
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </nav>

            <main className="pt-32 pb-24 px-6">
                <div className="max-w-4xl mx-auto">
                    <Link to="/" className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors mb-8">
                        <ArrowLeft size={20} />
                        {t('help.nav.back_home')}
                    </Link>

                    <div className="text-center mb-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-blue-500/10 text-blue-500 mb-6">
                            <HelpCircle size={40} />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
                            {t('help.hero.title')}
                        </h1>
                    </div>

                    {/* Categories Grid */}
                    <div className="grid md:grid-cols-2 gap-6 mb-16">
                        {categories.map((cat, i) => (
                            <Link to={cat.link} key={i} className="bg-dark-card border border-dark-border rounded-2xl p-6 hover:border-primary/50 transition-all hover:-translate-y-1 group">
                                <div className="w-12 h-12 bg-dark-bg rounded-xl flex items-center justify-center text-text-main mb-4 group-hover:text-primary transition-colors">
                                    <cat.icon size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-text-main mb-2">{cat.title}</h3>
                                <p className="text-text-muted text-sm leading-relaxed">{cat.desc}</p>
                            </Link>
                        ))}
                    </div>

                    {/* FAQ Rapide */}
                    <div className="mb-16">
                        <h2 className="text-2xl font-bold text-text-main mb-8">{t('help.faq.title')}</h2>
                        <div className="space-y-4">
                            {faqs.map((faq, i) => (
                                <details key={i} className="group bg-dark-card border border-dark-border rounded-xl p-4 cursor-pointer open:bg-dark-hover transition-colors">
                                    <summary className="font-semibold text-text-main list-none flex justify-between items-center">
                                        {faq.q}
                                        <ArrowLeft className="rotate-[-90deg] group-open:rotate-90 transition-transform" size={16} />
                                    </summary>
                                    <p className="mt-4 text-text-muted text-sm leading-relaxed border-t border-dark-border pt-4">
                                        {faq.a}
                                    </p>
                                </details>
                            ))}
                        </div>
                    </div>

                    {/* Contact CTA */}
                    <div className="bg-dark-card border border-dark-border rounded-3xl p-8 text-center">
                        <h3 className="text-xl font-bold text-text-main mb-2">{t('help.contact.title')}</h3>
                        <p className="text-text-muted mb-6">{t('help.contact.desc')}</p>
                        <Link to="/legal/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover transition-colors">
                            <Mail size={20} />
                            {t('help.contact.cta')}
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
}
